import TenantCategories from './TenantCategories';
import { getPortalActiveRole } from '../../auth/portalSession';
import CancellationPolicy from './CancellationPolicy';
import { useAtomValue } from 'jotai';
import { profileDataAtom } from '../../atoms/authAtoms';
import { appRoles } from '../../authConfig';
import { PrimaryButton, Stack, Text } from '@fluentui/react';
import { makeStyles, Tab } from '@fluentui/react-components';
import ScrollableTabList from './ScrollableTabList';
import { PagedDataGrid, useFetch, type DataGridColumn } from '@moko/ui';
import { useEffect, useState } from 'react';
import { useSetAtom } from 'jotai';
import { useLocation, useNavigate } from 'react-router-dom';
import CompanyHolidays from './CompanyHolidays';
import GlobalPricingParameters from './GlobalPricingParameters';
import PricingItems from './PricingItems';
import PricingRates from './PricingRates';
import TimeServices from './TimeServices';
import {
  errorMessageAtom,
  isInProgressAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';
import {
  getBillingClasses,
  getCertificationTypes,
  getPricingTiers,
  getWorkTypes,
  IBillingClass,
  ICertificationType,
  IPricingTier,
  IWorkType,
  resetCache,
} from '../../services/settingsService';

type PricingTierRow = IPricingTier & { id: number };
type WorkTypeRow = IWorkType & { id: number };
type CertificationTypeRow = ICertificationType & { id: number };
type BillingClassRow = IBillingClass & { id: number };

const pricingColumns: DataGridColumn<PricingTierRow>[] = [
  { key: 'code', name: 'Code', minWidth: 80, width: 140 },
  { key: 'name', name: 'Name', minWidth: 160, width: 280 },
  {
    key: 'discountPercentage',
    name: 'Discount %',
    minWidth: 90,
    width: 120,
    value: (item) =>
      item.discountPercentage == null ? '' : `${item.discountPercentage}%`,
  },
  {
    key: 'minOrderCount',
    name: 'Min order count',
    minWidth: 120,
    width: 160,
    value: (item) =>
      item.minOrderCount == null ? '' : String(item.minOrderCount),
  },
];

const catalogColumns: DataGridColumn<WorkTypeRow>[] = [
  { key: 'code', name: 'Code', minWidth: 260, width: 320 },
  { key: 'name', name: 'Name', minWidth: 280, width: 460 },
];

const billingClassColumns: DataGridColumn<BillingClassRow>[] = [
  { key: 'code', name: 'Code', minWidth: 80, width: 140 },
  { key: 'name', name: 'Name', minWidth: 160, width: 280 },
  {
    key: 'description',
    name: 'Description',
    minWidth: 200,
    width: 420,
    value: (item) => item.description ?? '',
  },
];

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: '28px',
    padding: '10px',
    minWidth: 0,
  },
});

const toRows = <T extends { id?: number; sortOrder: number }>(rows: T[]) =>
  [...(rows ?? [])]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((row, index) => ({
      ...row,
      id: row.id ?? index + 1,
    }));

const OtherSettings = () => {
  const { roles } = useAtomValue(profileDataAtom);
  const { search } = useLocation();
  const navigate = useNavigate();
  const selectedTab = new URLSearchParams(search).get('tab') ?? 'pricingTiers';
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const setIsInProgress = useSetAtom(isInProgressAtom);
  const [isResettingCache, setIsResettingCache] = useState(false);
  const [pricingTiers, setPricingTiers] = useState<PricingTierRow[]>([]);
  const [pricingLoading, setPricingLoading] = useState(true);
  const [workTypes, setWorkTypes] = useState<WorkTypeRow[]>([]);
  const [workTypesLoading, setWorkTypesLoading] = useState(true);
  const [workTypesError, setWorkTypesError] = useState<string>();
  const [certificationTypes, setCertificationTypes] = useState<
    CertificationTypeRow[]
  >([]);
  const [certificationTypesLoading, setCertificationTypesLoading] =
    useState(true);
  const [certificationTypesError, setCertificationTypesError] =
    useState<string>();
  const billingClassFetch = useFetch<IBillingClass[]>({
    queryKey: ['billingClasses'],
    queryFn: getBillingClasses,
    onError: (error) =>
      setErrorMessage(error.message || 'Failed to load billing classes.'),
  });

  const handleResetCache = async () => {
    const abortController = new AbortController();

    setIsResettingCache(true);
    setIsInProgress(true);

    try {
      await resetCache(abortController);
      setSuccessMessage('Cache reset event published successfully.');
    } catch (error: any) {
      setErrorMessage(error?.message || 'Failed to reset cache.');
    } finally {
      setIsInProgress(false);
      setIsResettingCache(false);
      abortController.abort();
    }
  };

  useEffect(() => {
    const abortController = new AbortController();

    getPricingTiers(abortController)
      .then((tiers) => setPricingTiers(toRows(tiers)))
      .catch((error: { message?: string }) => {
        if (abortController.signal.aborted) return;
        setErrorMessage(error?.message || 'Failed to load pricing tiers.');
      })
      .finally(() => {
        if (!abortController.signal.aborted) setPricingLoading(false);
      });

    getWorkTypes(abortController)
      .then((types) => setWorkTypes(toRows(types)))
      .catch((error: { message?: string }) => {
        if (abortController.signal.aborted) return;
        const message = error?.message || 'Failed to load work types.';
        setWorkTypesError(message);
        setErrorMessage(message);
      })
      .finally(() => {
        if (!abortController.signal.aborted) setWorkTypesLoading(false);
      });

    getCertificationTypes(abortController)
      .then((types) => setCertificationTypes(toRows(types)))
      .catch((error: { message?: string }) => {
        if (abortController.signal.aborted) return;
        const message = error?.message || 'Failed to load certification types.';
        setCertificationTypesError(message);
        setErrorMessage(message);
      })
      .finally(() => {
        if (!abortController.signal.aborted)
          setCertificationTypesLoading(false);
      });

    return () => abortController.abort();
  }, [setErrorMessage]);

  const styles = useStyles();
  const tabs = [
    { key: 'pricingTiers', label: 'Pricing tiers' },
    { key: 'pricingItems', label: 'Pricing items' },
    { key: 'pricingRates', label: 'Pricing rates' },
    { key: 'pricingParameters', label: 'Pricing parameters' },
    { key: 'timeServices', label: 'Time services' },
    { key: 'holidays', label: 'Holidays' },
    { key: 'workTypes', label: 'Work types' },
    { key: 'certificationTypes', label: 'Certification types' },
    { key: 'billingClasses', label: 'Billing classes' },
    ...(roles.includes(appRoles.Admin)
      ? [{ key: 'cancellationPolicy', label: 'Cancellation policy' }]
      : []),
    ...(getPortalActiveRole() === 'FMSPortal.AdminAccess'
      ? [{ key: 'categories', label: 'Categories' }]
      : []),
    { key: 'maintenance', label: 'Maintenance' },
  ];
  const activeTab = tabs.some((tab) => tab.key === selectedTab)
    ? selectedTab
    : 'pricingTiers';

  return (
    <div className={styles.page}>
      <ScrollableTabList
        selectedValue={activeTab}
        onTabSelect={(value) =>
          navigate(`/settings/other?tab=${value}`, { replace: true })
        }
      >
        {tabs.map((tab) => (
          <Tab key={tab.key} value={tab.key}>
            {tab.label}
          </Tab>
        ))}
      </ScrollableTabList>
      {activeTab === 'pricingTiers' && (
        <Stack tokens={{ childrenGap: 12 }}>
          <PagedDataGrid<PricingTierRow>
            columns={pricingColumns}
            items={pricingTiers}
            loading={pricingLoading}
          />
        </Stack>
      )}
      {activeTab === 'pricingItems' && <PricingItems />}
      {activeTab === 'pricingRates' && <PricingRates />}
      {activeTab === 'pricingParameters' && <GlobalPricingParameters />}
      {activeTab === 'timeServices' && <TimeServices />}
      {activeTab === 'holidays' && <CompanyHolidays />}
      {activeTab === 'workTypes' && (
        <Stack tokens={{ childrenGap: 12 }}>
          {workTypesError ? (
            <Text>{workTypesError}</Text>
          ) : (
            <PagedDataGrid<WorkTypeRow>
              columns={catalogColumns}
              items={workTypes}
              loading={workTypesLoading}
            />
          )}
        </Stack>
      )}
      {activeTab === 'certificationTypes' && (
        <Stack tokens={{ childrenGap: 12 }}>
          {certificationTypesError ? (
            <Text>{certificationTypesError}</Text>
          ) : (
            <PagedDataGrid<CertificationTypeRow>
              columns={catalogColumns}
              items={certificationTypes}
              loading={certificationTypesLoading}
            />
          )}
        </Stack>
      )}
      {activeTab === 'billingClasses' && (
        <Stack tokens={{ childrenGap: 12 }}>
          <PagedDataGrid<BillingClassRow>
            columns={billingClassColumns}
            items={(billingClassFetch.data ?? []).map((row, index) => ({
              ...row,
              id: row.id ?? index + 1,
            }))}
            loading={billingClassFetch.loading}
          />
        </Stack>
      )}
      {activeTab === 'cancellationPolicy' && roles.includes(appRoles.Admin) && (
        <CancellationPolicy />
      )}
      {activeTab === 'categories' &&
        getPortalActiveRole() === 'FMSPortal.AdminAccess' && (
          <TenantCategories />
        )}
      {activeTab === 'maintenance' && (
        <Stack tokens={{ childrenGap: 12 }}>
          <Text>
            Publish a reset-cache event to clear all in-memory cache entries.
          </Text>
          <Stack horizontal>
            <PrimaryButton
              text={isResettingCache ? 'Resetting...' : 'Reset Cache'}
              onClick={handleResetCache}
              disabled={isResettingCache}
            />
          </Stack>
        </Stack>
      )}
    </div>
  );
};

export default OtherSettings;
